import React, { Fragment, useCallback, useMemo, useState } from 'react';
import DataGridTable from '../../Components/DataGridTable';
import { getNextTemporaryDatastreamId } from '../../utils/datastreamIds';
import { CreateDataStreamIcon, EditEvaluationsIcon } from '../../svg/TemplateIcons';
import { DeleteIcon, DuplicateIcon } from '../../svg/UtilsIcons';
import {
    buildUniqueDatastreamCopyName,
    getNextAvailableVirtualPin,
} from './templateDatastreamHelpers';

const TemplateDatastreamsTab = ({
    details,
    setDetails,
    edit,
    setAddDatastream,
    templateEvaluationData,
    setTemplateEvaluationData,
    setIsEvalModalOpen,
    canEditTemplate,
    canEditDatastream,
    canDeleteDatastream,
    t,
    pagingoptions,
    handleRowClick,
    selectedRows,
    setSelectedRows,
    size,
    pinCategories,
    fields
}) => {
    const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);

    const rows = details.datastreams?.filter(ds => ds) ?? [];

    const FakeIcon = () => (<span className="td-fake-icon" />);

    const handleNewDatastreamCategory = cat => {
        setIsCategoriesOpen(false);
        setAddDatastream({
            edit: true,
            addEvents: false,
            pinCategory: cat,
            isNew: true
        });
    };

    const getNextDatastreamIdForTab = list => {
        return getNextTemporaryDatastreamId(list);
    };

    const duplicateSelectedDatastreams = useCallback((sourceIds) => {
        const normalizedIds = Array.from(
            new Set((Array.isArray(sourceIds) ? sourceIds : [sourceIds])
                .map(id => Number(id))
                .filter(id => Number.isFinite(id)))
        );

        if (normalizedIds.length === 0) return;

        const existing = (details.datastreams || []).filter(Boolean);
        const sourceMap = new Map(existing.map(ds => [Number(ds.id), ds]));
        const sources = normalizedIds
            .map(id => sourceMap.get(id))
            .filter(Boolean);

        if (sources.length === 0) return;

        const usedPins = new Set(existing.map(d => d.pin).filter(Boolean));
        const prevRanges = templateEvaluationData?.datastream_ranges || [];
        const clonedDatastreams = [];
        const clonedRanges = [];
        let nextId = getNextDatastreamIdForTab(existing);
        let nextReportPriority = Math.max(
            0,
            ...existing.map(datastream => Number(datastream?.reportPriority) || 0)
        ) + 1;

        sources.forEach(source => {
            let newPin = source.pin;
            if (!newPin || usedPins.has(newPin)) {
                newPin = getNextAvailableVirtualPin(usedPins);
            }
            if (newPin) {
                usedPins.add(newPin);
            }

            const newId = nextId++;
            clonedDatastreams.push({
                ...source,
                id: newId,
                pin: newPin,
                name: buildUniqueDatastreamCopyName(source?.name, [...existing, ...clonedDatastreams]),
                reportPriority: nextReportPriority++,
            });

            prevRanges
                .filter(r => Number(r.datastream_id) === Number(source.id))
                .forEach(r => {
                    clonedRanges.push({
                        ...r,
                        datastream_evaluation_id: null,
                        datastream_id: newId,
                    });
                });
        });

        if (clonedDatastreams.length === 0) return;

        setDetails(prev => ({
            ...prev,
            datastreams: [...(prev?.datastreams || []).filter(Boolean), ...clonedDatastreams],
        }));

        if (clonedRanges.length > 0) {
            setTemplateEvaluationData(prevData => ({
                ...prevData,
                datastream_ranges: [...(prevData?.datastream_ranges || []), ...clonedRanges],
            }));
        }

        setSelectedRows([]);
    }, [details.datastreams, setDetails, setSelectedRows, setTemplateEvaluationData, templateEvaluationData?.datastream_ranges]);

    const handleDeleteSelected = useCallback(() => {
        if (!canDeleteDatastream) return;
        if (selectedRows.length === 0) return;

        if (!window.confirm(`${t.confirmDeleteSelected || 'Are you sure you want to delete'} ${selectedRows.length} ${t.selectedDatastreams || 'selected datastream(s)'}?`)) {
            return;
        }

        const datastreamIdsToDelete = new Set(
            selectedRows
                .map(id => Number(id))
                .filter(id => Number.isFinite(id))
        );

        setDetails(prev => ({
            ...prev,
            datastreams: prev.datastreams.filter(ds => !datastreamIdsToDelete.has(ds.id))
        }));

        setTemplateEvaluationData(prevData => ({
            ...prevData,
            datastream_ranges: (prevData?.datastream_ranges || []).filter(
                r => !datastreamIdsToDelete.has(r.datastream_id)
            )
        }));

        setSelectedRows([]);
    }, [canDeleteDatastream, selectedRows, setDetails, setSelectedRows, setTemplateEvaluationData, t]);

    const toolbarSelectActions = useMemo(() => {
        const duplicateAction = {
            icon: <DuplicateIcon style={{ color: 'var(--accent)' }} />,
            title: t.duplicateDatastream || t.duplicate || 'Duplicate Selected',
            action: duplicateSelectedDatastreams,
        };

        const deleteAction = {
            icon: <DeleteIcon style={{ color: 'var(--red-color, rgb(236, 69, 78))' }} />,
            title: t.deleteSelected || 'Delete Selected',
            action: handleDeleteSelected,
        };

        return [
            ...(canEditDatastream ? [duplicateAction] : []),
            ...(canDeleteDatastream ? [deleteAction] : []),
        ];
    }, [canDeleteDatastream, canEditDatastream, t, handleDeleteSelected, duplicateSelectedDatastreams]);

    return (
        <Fragment>
            <div className="td-panel">
                <div className="td-panel__top">
                    <h5 className="td-panel__title">
                        {t.datastreamsHeader}
                    </h5>
                </div>

                {edit.mode === 'edit' && canEditTemplate && (
                    <div className="td-panel__actions">
                        <button type="button" onClick={() => setIsEvalModalOpen(true)} className="td-primary-btn td-primary-btn--compact">
                            <EditEvaluationsIcon />
                            <span>{t.editEvaluations || 'Edit Evaluations'}</span>
                        </button>

                        <div className="td-menu-wrapper">
                            <button type="button" onClick={() => setIsCategoriesOpen(prev => !prev)} className="td-primary-btn td-primary-btn--compact">
                                <CreateDataStreamIcon />
                                <span>{t.newDatastream}</span>
                            </button>

                            {isCategoriesOpen && (
                                <ul className="td-category-menu">
                                    {pinCategories.map((cat, index) => (
                                        <li key={index} className="td-category-item" onClick={() => handleNewDatastreamCategory(cat)}>
                                            <FakeIcon />
                                            <span>{cat}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                )}

                <div
                    className="table-wrapper"
                    style={{
                        flex: 1,
                        minHeight: 0,
                        marginTop: 4
                    }}
                >
                    <DataGridTable
                        isDetail={true}
                        columns={fields}
                        rows={rows}
                        pagingoptions={pagingoptions}
                        showToolbar={true}
                        handleRowClick={handleRowClick}
                        idcolumn={'id'}
                        checkboxSelection={edit.mode === 'edit' && (canEditDatastream || canDeleteDatastream)}
                        rowSelectionModel={selectedRows}
                        setRowSelectionModel={setSelectedRows}
                        selectActions={toolbarSelectActions}
                        size={size}
                        tableKey="templateDatastream"
                    />
                </div>
            </div>
        </Fragment>
    );
};

export default TemplateDatastreamsTab;
